import { Injectable } from "@angular/core";
import {
  Contact,
  ContactTypeWithRelations,
  ContactWithRelations,
  ItemOwnerWithRelations,
  ItemWithRelations,
} from "src/app/api/models";
import { AuthorService } from "../shared/services/author.service";
import { CompareService } from "../shared/services/compare.service";
import { Sort } from "@angular/material/sort";
import { ItemFilter } from "src/app/pages/inventory/manager/items/list/inventory-items-list.component";

export const REMOVAL_TYPE_LABELS: Record<string, string> = {
  sold: 'Prodáno',
  lost: 'Ztraceno',
  stolen: 'Odcizeno',
  destroyed: 'Zničeno',
  donated: 'Darováno',
};

export const AVAILABLE_SORT_COLUMNS: string[] = [
  "inventoryCode",
  "originalTitle",
  "dating",
  "datingOrder",
  "technique",
  "substrate",
  "authors",
];

@Injectable({
  providedIn: "root",
})
export class ItemService {
  constructor(
    private authorService: AuthorService,
    private compareService: CompareService,
  ) {}

  getRemovalTypeLabel(type: string): string {
    return REMOVAL_TYPE_LABELS[type] ?? type;
  }

  /**
   * Parses the title of an item, optionally including the author.
   *
   * This method constructs the title string by concatenating the inventory code,
   * author (if included), and the original title of the item.
   *
   * @param item - The item for which to parse the title.
   * @param includeAuthor - Whether to include the author in the title.
   * @param lang - The language code for parsing the author name.
   * @returns The parsed title string.
   */
  parseItemTitle(
    item: ItemWithRelations,
    includeAuthor: boolean = false,
    lang: string = "cs",
  ): string {
    let title = "";
    title += item.inventoryCode;
    title += " - ";
    if (includeAuthor) {
      const authorString = this.parseItemAuthors(item, lang);
      title += authorString;
      title += " - ";
    }
    title += item.originalTitle;
    return title;
  }

  /**
   * Parses the authors of an item into a string.
   *
   * This method constructs the authors string based on the number of authors and the specified language.
   * It uses predefined constants for "no author" and "multiple authors" scenarios.
   *
   * @param item - The item for which to parse the authors.
   * @param lang - The language code for parsing the author names.
   * @returns The parsed authors string.
   */
  parseItemAuthors(item: ItemWithRelations, lang?: string): string {
    let authors = "";
    const { NO_AUTHOR, MORE_AUTHORS } = this.authorService;
    if (item.authors) {
      switch (item.authors.length) {
        case 0:
          authors =
            lang && (lang === "cs" || lang === "en")
              ? NO_AUTHOR[lang]
              : NO_AUTHOR.cs;
          break;
        case 1:
          authors = this.authorService.parseAuthorName(item.authors[0]);
          break;
        default:
          authors =
            lang && (lang === "cs" || lang === "en")
              ? MORE_AUTHORS[lang]
              : MORE_AUTHORS.cs;
          break;
      }
    } else {
      authors =
        lang && (lang === "cs" || lang === "en")
          ? NO_AUTHOR[lang]
          : NO_AUTHOR.cs;
    }
    return authors;
  }

  /**
   * Parses the authors of an item into a tooltip string.
   *
   * This method constructs the tooltip string by concatenating the names of all authors,
   * separated by commas.
   *
   * @param item - The item for which to parse the authors tooltip.
   * @returns The parsed authors tooltip string.
   */
  parseItemAuthorsTooltip(item: ItemWithRelations): string {
    let tooltip = "";
    if (item.authors) {
      item.authors.forEach((author, index, array) => {
        tooltip +=
          this.authorService.parseAuthorName(author) +
          `${index !== array.length - 1 ? ", " : ""}`;
      });
    }
    return tooltip;
  }

  /**
   * Parses the dating information of an item.
   *
   * This method retrieves the dating information from the item's translations based on the locale ID.
   * If no translation is found, it falls back to the item's dating order.
   *
   * @param item - The item for which to parse the dating information.
   * @returns The parsed dating string.
   */
  parseItemDating(item: ItemWithRelations): string {
    let dating = "";
    const usedTranslation = item.translations?.find(
      (translation) => translation.localeId === 1,
    );
    if (usedTranslation) {
      dating = usedTranslation.dating;
    } else {
      dating = item.datingOrder ? String(item.datingOrder) : "";
    }
    return dating;
  }

  /**
   * Retrieves the owner of the specified item using a prioritized sorting of its associated owners.
   *
   * The method performs the following steps:
   * 1. If the item has no owners, returns undefined immediately.
   * 2. Sorts the owners by the sort order of their owner type. If two owners share the same sort order,
   *    they are further sorted by the year of purchase (assumed in descending order).
   * 3. Depending on the desired owner type:
   *    - "current": Selects the first (latest) owner, but only if its ownerTypeId is not 2.
   *    - "original": Selects the last (oldest) owner, but only if its ownerTypeId is exactly 2.
   * 4. If the selected owner has a contactId, retrieves the complete contact details from the provided contacts array.
   *    - For the special cases "unknown" and "anonym", default Contact objects are created.
   *    - Otherwise, it finds the corresponding Contact in the contacts array.
   * 5. Returns the result in the format specified by resultType:
   *    - "complete": Returns the complete Contact object.
   *    - "simple": Returns a simplified string label derived by looking up the contact type title;
   *                if not available, it falls back to the owner type title.
   *
   * @param item - The item for which to retrieve owner information.
   * @param resultType - Specifies whether to return a complete Contact object ("complete") or a simplified string ("simple").
   * @param ownerType - Indicates which owner to select: "current" for the latest valid owner, or "original" for the oldest (if valid).
   * @param contactTypes - An array of contact type objects used to derive the simplified owner label.
   * @param contacts - An array of contacts used to obtain full owner details.
   * @returns The owner of the item, either as a complete Contact object or a simplified string label, or undefined if no valid owner is found.
   */
  getOwner(
    item: ItemWithRelations,
    resultType: "complete" | "simple",
    ownerType: "current" | "original",
    contactTypes: ContactTypeWithRelations[],
    contacts: ContactWithRelations[],
  ): Contact | string | undefined {
    let simpleOwner: string | undefined;
    let completeOwner: Contact | undefined;
    let owner: ItemOwnerWithRelations | undefined;

    // Step 1: Return undefined immediately if the item has no owners.
    if (!item.owners || item.owners.length === 0) return undefined;

    // Step 2: Sort owners by the sort order of their owner type.
    //         If two owners have the same sort order, sort them by year of purchase (descending order).
    const sortedOwners = item.owners.sort((a, b) => {
      if (a.ownerType?.sortOrder === b.ownerType?.sortOrder) {
        return this.compareService.compare(
          a.yearPurchase,
          b.yearPurchase,
          true,
        );
      } else {
        return this.compareService.compare(
          a.ownerType?.sortOrder,
          b.ownerType?.sortOrder,
          true,
        );
      }
    });

    // Step 3: Select the owner based on the desired owner type.
    switch (ownerType) {
      case "current": {
        // For "current", choose the latest owner if its ownerTypeId is not equal to 2.
        const latestOwnerInList = sortedOwners[0];
        owner =
          latestOwnerInList.ownerTypeId !== 2 ? latestOwnerInList : undefined;
        break;
      }
      case "original": {
        // For "original", choose the oldest owner only if its ownerTypeId is equal to 2.
        const oldestOwnerInList = sortedOwners[sortedOwners.length - 1];
        owner =
          oldestOwnerInList.ownerTypeId === 2 ? oldestOwnerInList : undefined;
        break;
      }
    }

    // If the owner exists but does not fall into the special owner types 1, 2, or 99, default to the owner type title.
    if (owner && ![1, 2, 99].includes(owner.ownerTypeId)) {
      simpleOwner = owner.ownerType?.title;
    } else {
      // If the owner has a contactId, resolve the complete contact information.
      if (owner?.contactId) {
        switch (owner.contactId) {
          case "unknown":
            completeOwner = { id: "unknown", title: "Nezjištěný majitel" };
            simpleOwner = "Nezjištěný majitel";
            break;
          case "anonym":
            completeOwner = { id: "anonym", title: "Anonymní majitel" };
            simpleOwner = "Anonymní majitel";
            break;
          default:
            // Retrieve complete contact details from the contacts array using the contactId.
            completeOwner = contacts.find((a) => a.id === owner.contactId);
            // Attempt to derive a simplified label using the contact type title.
            simpleOwner = contactTypes.find(
              (a) => a.id === completeOwner?.contactTypeId,
            )?.title;
            // Fallback: If no contact type title is found, use the owner type title.
            if (!simpleOwner && owner.ownerType) {
              simpleOwner = owner.ownerType.title;
            }
            break;
        }
      }
    }

    // Step 5: Return the result based on the requested result type.
    switch (resultType) {
      case "complete":
        return completeOwner;
      case "simple":
        return simpleOwner;
    }
  }

  /**
   * Sorts an array of items based on the provided sort criteria.
   *
   * @param {ItemWithRelations[]} data - Array of items to sort.
   * @param {Sort} sort - Object containing the active sort column and the sort direction.
   * @returns {ItemWithRelations[]} The sorted array of items.
   */
  customSortItems(data: ItemWithRelations[], sort: Sort): ItemWithRelations[] {
    let sortedData = [];
    // Determine if the sort order is ascending.
    const isAsc = sort.direction === "asc";

    // If no sort column or direction is provided, return the original data.
    if (!sort.active || sort.direction === "") {
      sortedData = data;
      return sortedData;
    }

    // Sort the data based on the active sort column.
    switch (sort.active) {
      case "inventoryCode":
        // Sort by inventoryNumber property.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.inventoryNumber,
            b.inventoryNumber,
            isAsc,
          );
        });
        break;
      case "originalTitle":
        // Sort by originalTitle property.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.originalTitle,
            b.originalTitle,
            isAsc,
          );
        });
        break;
      case "dating":
        // Sort by the dating property from the first translation.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.translations[0]?.dating,
            b.translations[0]?.dating,
            isAsc,
          );
        });
        break;
      case "technique":
        // Sort by the technique property from the first translation.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.translations[0]?.technique,
            b.translations[0]?.technique,
            isAsc,
          );
        });
        break;
      case "substrate":
        // Sort by the substrate property from the first translation.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.translations[0]?.substrate,
            b.translations[0]?.substrate,
            isAsc,
          );
        });
        break;
      case "datingOrder":
        // Sort by datingOrder property.
        sortedData = data.sort((a, b) => {
          return this.compareService.compare(
            a.datingOrder,
            b.datingOrder,
            isAsc,
          );
        });
        break;
      case "authors":
        // Sort by the authors property.
        sortedData = data.sort((a, b) => {
          const aString = this.authorService.getAuthorStringForSort(
            a.authors[0],
          );
          const bString = this.authorService.getAuthorStringForSort(
            b.authors[0],
          );
          return this.compareService.compare(aString, bString, isAsc);
        });
        break;
      default:
        // If the sort column is not recognized, return the original data.
        sortedData = data;
        break;
    }
    return sortedData;
  }

  /**
   * Filters the items based on the provided filter settings.
   *
   * This method applies various filtering criteria to the items array, including series, item types,
   * key publication status, and signature status. It supports two match modes:
   * - "matchAny": Includes items if at least one criterion matches.
   * - "matchAll": Includes items only if all active criteria are matched.
   *
   * @param data - The array of ItemWithRelations objects to be filtered.
   * @param filter - The filter settings to apply.
   * @param itemTypes - The array of ItemTypeWithRelations objects to filter by.
   * @param keyPublicationId - The optional key publication ID to filter by.
   * @returns The filtered array of ItemWithRelations objects.
   */
  filterItems(
    data: ItemWithRelations[],
    filter: ItemFilter,
    keyPublicationId?: string,
  ): ItemWithRelations[] {
    let filteredData = [];
    // If the filter is inactive, display all items
    if (!filter.active) {
      filteredData = data;
      return filteredData;
    }

    const COMPLETION_KEYS: (keyof ItemFilter)[] = [
      "seen",
      "estate",
      "published",
      "printQuality",
      "permissionReproduce",
      "translated",
      "completed",
      "public",
    ];

    // Pre-compute how many criteria are active (constant across all items)
    const totalActiveFilters = [
      filter.series?.length > 0,
      filter.itemTypes?.length > 0,
      filter.keyPublication !== null,
      filter.signature?.length > 0,
      filter.auctions?.length > 0,
      filter.exhibitions?.length > 0,
      filter.publications?.length > 0,
      filter.tags?.length > 0,
      ...COMPLETION_KEYS.map((k) => filter[k] !== null),
    ].filter(Boolean).length;

    // If no criteria are set, show all items regardless of match mode
    if (totalActiveFilters === 0) return data;

    // Apply filtering based on active criteria and match mode
    filteredData = data.filter((item) => {
      let matchCount = 0; // Tracks criteria matches for "matchAll" mode

      const matchAll = filter.matchMode === "matchAll";

      // Series filter
      // matchAll: item must belong to ALL selected series (AND within series)
      // matchAny: item must belong to ANY selected series (OR within series)
      if (filter.series?.length > 0) {
        const itemSeriesIds = item.series?.map((s) => s.id) ?? [];
        const matches = matchAll
          ? filter.series.every((id) => itemSeriesIds.includes(id))
          : filter.series.some((id) => itemSeriesIds.includes(id));
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Item Types filter
      if (filter.itemTypes?.length > 0) {
        const matches = matchAll
          ? filter.itemTypes.every((id) => item.itemTypeId === id)
          : filter.itemTypes.includes(item.itemTypeId);
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Key Publication filter (boolean — no multi-value, same logic for both modes)
      if (filter.keyPublication !== null && keyPublicationId) {
        const itemInKeyPublication = this.checkIfItemInKeyPublication(
          item,
          keyPublicationId,
        );
        const matches =
          (filter.keyPublication && itemInKeyPublication) ||
          (!filter.keyPublication && !itemInKeyPublication);
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Signature filter
      // matchAll: all selected types must match (only meaningful with a single selection)
      // matchAny: any selected type matches
      if (filter.signature?.length > 0) {
        const itemSignatureType = this.getSignature(item).type;
        const matches = matchAll
          ? filter.signature.every((t) => t === itemSignatureType)
          : filter.signature.includes(itemSignatureType);
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Auctions filter
      if (filter.auctions?.length > 0) {
        const itemAuctionIds = item.auctions?.map((a) => a.id) ?? [];
        const matches = matchAll
          ? filter.auctions.every((id) => itemAuctionIds.includes(id))
          : filter.auctions.some((id) => itemAuctionIds.includes(id));
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Exhibitions filter
      if (filter.exhibitions?.length > 0) {
        const itemExhibitionIds = item.exhibitions?.map((e) => e.id) ?? [];
        const matches = matchAll
          ? filter.exhibitions.every((id) => itemExhibitionIds.includes(id))
          : filter.exhibitions.some((id) => itemExhibitionIds.includes(id));
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Publications filter
      if (filter.publications?.length > 0) {
        const itemPublicationIds = item.publications?.map((p) => p.id) ?? [];
        const matches = matchAll
          ? filter.publications.every((id) => itemPublicationIds.includes(id))
          : filter.publications.some((id) => itemPublicationIds.includes(id));
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Tags filter: item must have the selected tag OR any of its descendants
      if (filter.tags?.length > 0) {
        const itemTagIds = item.tags?.map((t) => t.id) ?? [];
        const matches = matchAll
          ? filter.tags.every((id) => itemTagIds.includes(id))
          : filter.tags.some((id) => itemTagIds.includes(id));
        if (matches) {
          matchCount++;
        } else if (matchAll) {
          return false;
        }
      }

      // Completion fields — each active field is its own independent criterion
      for (const k of COMPLETION_KEYS) {
        if (filter[k] !== null) {
          const matches = (item as any)[k] === filter[k];
          if (matches) {
            matchCount++;
          } else if (matchAll) {
            return false;
          }
        }
      }

      return filter.matchMode === "matchAny"
        ? matchCount > 0
        : matchCount === totalActiveFilters;
    });
    return filteredData;
  }

  /**
   * Returns the appropriate icon and tooltip for an item's signature status.
   * The function checks if the item has any signature information and determines the icon and tooltip
   * based on the type of signature.
   *
   * @param item - The item for which to retrieve the signature status.
   * @returns An object containing the icon and tooltip text.
   */
  getSignature(item: ItemWithRelations): {
    icon: string;
    tooltip: string;
    type: string;
  } {
    // Default values when no signature is available
    let icon = "mat:remove_circle_outline";
    let tooltip = "Dílo nemá vybranou signaturu";
    let type = "notSelected";

    // If the item has signature information, proceed to check the signature type
    if (item.signatures) {
      /**
       * signatureTypeIds:
       * 1 - signed
       * 2 - not-signed
       * 3 - unknown
       */
      switch (item.signatures[0].signatureTypeId) {
        case 1: // Signed
          icon = "mat:check_circle";
          tooltip = "Dílo je signováno";
          type = "signed";
          break;
        case 2: // Not signed
          icon = "mat:highlight_off";
          tooltip = "Dílo není signováno";
          type = "notSigned";
          break;
        case 3: // Unknown
          icon = "mat:help_outline";
          tooltip = "Signatura nezjištěna";
          type = "unknown";
          break;
      }
    }

    // Return the icon and tooltip based on the signature type
    return { icon, tooltip, type };
  }

  /**
   * Checks if an item is featured in the key publication.
   *
   * This method determines whether a given item appears in the key publication
   * associated with the current inventory. It returns:
   * - `true` if the item is listed in the key publication,
   * - `false` if the item has publications but is not in the key publication, or
   * - `null` if there is no key publication set in the inventory.
   *
   * @param item - The item to check, containing its list of publications.
   * @returns `true` if the item is in the key publication, `false` otherwise, and `null` if no key publication is defined.
   */
  checkIfItemInKeyPublication(
    item: ItemWithRelations,
    keyPublicationId: string,
  ): boolean | null {
    // Return null if no key publication is set in the inventory
    if (!keyPublicationId) {
      return null;
    }

    // Check if item has associated publications
    if (item.publications) {
      // Return true if any publication ID matches the key publication ID
      return item.publications.some(
        (publication) => publication.id === keyPublicationId,
      );
    }

    // Return false if item has no publications
    return false;
  }

  getCompleteInventoryCode(item: ItemWithRelations): string {
    let inventoryCode = item.inventoryCode;
    if (item.itemDisputationType) {
      inventoryCode += item.itemDisputationType.shortcut;
    }
    return inventoryCode;
  }
}
